import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import {
  getAdminServices, updateAdminService, toggleAllServices,
  addServiceDisabledUser, removeServiceDisabledUser, updateServiceCountries,
} from "../../../api/apiServices";


function ConfirmModal({ title, message, confirmLabel, confirmColor = "red", onConfirm, onCancel, loading }) {
  const btnCls = { red: "bg-red-600 hover:bg-red-700 text-white", green: "bg-[#00C896] hover:bg-[#00b386] text-black" }[confirmColor];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center space-y-2">
          <p className="text-white font-bold">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onCancel} disabled={loading} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${btnCls}`}>
            {loading ? <div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : null}
            {loading ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ svc, onToggle, toggling, onRefresh }) {
  const [editMsg,     setEditMsg]     = useState(false);
  const [msgDraft,    setMsgDraft]    = useState(svc.disabledMessage || "");
  const [savingMsg,   setSavingMsg]   = useState(false);
  const [expanded,    setExpanded]    = useState(false);

  // Country controls
  const [editCountries,     setEditCountries]     = useState(false);
  const [allowedDraft,      setAllowedDraft]      = useState((svc.allowedCountries || []).join(", "));
  const [disabledDraft,     setDisabledDraft]     = useState((svc.disabledCountries || []).join(", "));
  const [savingCountries,   setSavingCountries]   = useState(false);

  // User block controls
  const [blockUserId,  setBlockUserId]  = useState("");
  const [blockingUser, setBlockingUser] = useState(false);

  const parseCountries = (str) =>
    str.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);

  const handleSaveMsg = async () => {
    setSavingMsg(true);
    try {
      await updateAdminService(svc.key, { disabledMessage: msgDraft });
      toast.success("Message saved");
      setEditMsg(false);
      onRefresh();
    } catch { toast.error("Failed to save message"); }
    finally { setSavingMsg(false); }
  };

  const handleSaveCountries = async () => {
    setSavingCountries(true);
    try {
      await updateServiceCountries(svc.key, {
        allowedCountries:  parseCountries(allowedDraft),
        disabledCountries: parseCountries(disabledDraft),
      });
      toast.success("Country rules saved");
      setEditCountries(false);
      onRefresh();
    } catch { toast.error("Failed to save country rules"); }
    finally { setSavingCountries(false); }
  };

  const handleBlockUser = async () => {
    if (!blockUserId.trim()) return;
    setBlockingUser(true);
    try {
      await addServiceDisabledUser(svc.key, blockUserId.trim());
      toast.success(`User ${blockUserId.trim()} blocked from ${svc.name}`);
      setBlockUserId("");
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.message || "Failed to block user"); }
    finally { setBlockingUser(false); }
  };

  const handleUnblockUser = async (userId) => {
    try {
      await removeServiceDisabledUser(svc.key, userId);
      toast.success(`User ${userId} unblocked`);
      onRefresh();
    } catch { toast.error("Failed to unblock user"); }
  };

  const hasCountryRules = (svc.allowedCountries?.length > 0) || (svc.disabledCountries?.length > 0);
  const hasUserBlocks   = svc.disabledUsers?.length > 0;

  return (
    <div className={`bg-[#161616] border rounded-2xl p-5 transition-all ${
      svc.enabled ? "border-white/5" : "border-red-500/20 bg-red-500/5"
    }`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            svc.enabled ? "bg-[#00C896]/10" : "bg-red-500/10"
          }`}>
            <i className={`${svc.icon || "ri-settings-line"} text-xl ${svc.enabled ? "text-[#00C896]" : "text-red-400"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-semibold text-sm">{svc.name}</p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                svc.enabled ? "bg-[#00C896]/15 text-[#00C896]" : "bg-red-500/15 text-red-400"
              }`}>
                {svc.enabled ? "Active" : "Disabled"}
              </span>
              {hasCountryRules && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">
                  Country rules
                </span>
              )}
              {hasUserBlocks && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">
                  {svc.disabledUsers.length} user{svc.disabledUsers.length > 1 ? "s" : ""} blocked
                </span>
              )}
            </div>
            <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{svc.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Expand/collapse for advanced controls */}
          <button
            onClick={() => setExpanded(v => !v)}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-colors"
            title="Advanced controls"
          >
            <i className={`ri-settings-3-line text-sm transition-transform ${expanded ? "rotate-90" : ""}`} />
          </button>

          {/* Toggle */}
          <button
            onClick={() => onToggle(svc.key, !svc.enabled, svc.name)}
            disabled={toggling === svc.key}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
              svc.enabled ? "bg-[#00C896]" : "bg-white/15"
            }`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
              svc.enabled ? "left-6" : "left-0.5"
            }`} />
          </button>
        </div>
      </div>

      {/* Affects tags */}
      {svc.affects?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {svc.affects.map(a => (
            <span key={a} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/5 text-white/40 border border-white/5">
              {a}
            </span>
          ))}
        </div>
      )}

      {/* Disabled message editor */}
      {!svc.enabled && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <p className="text-white/30 text-[10px] uppercase tracking-wide font-semibold mb-2">Message shown to users</p>
          {editMsg ? (
            <div className="space-y-2">
              <textarea
                value={msgDraft}
                onChange={e => setMsgDraft(e.target.value)}
                rows={2}
                className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 resize-none"
              />
              <div className="flex gap-2">
                <button onClick={() => { setEditMsg(false); setMsgDraft(svc.disabledMessage); }}
                  className="flex-1 py-2 rounded-xl bg-white/5 text-white/40 text-xs font-semibold">Cancel</button>
                <button onClick={handleSaveMsg} disabled={savingMsg}
                  className="flex-1 py-2 rounded-xl bg-[#00C896] text-black text-xs font-bold disabled:opacity-50">
                  {savingMsg ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <p className="text-white/50 text-xs italic flex-1">"{svc.disabledMessage}"</p>
              <button onClick={() => setEditMsg(true)} className="text-[#00C896] text-[10px] font-bold flex-shrink-0 hover:opacity-70">Edit</button>
            </div>
          )}
        </div>
      )}

      {/* ── Advanced controls (expanded) ───────────────────── */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-white/5 space-y-5">

          {/* Country controls */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-white/40 text-[10px] uppercase tracking-wide font-semibold">Country Access Rules</p>
              <button onClick={() => setEditCountries(v => !v)}
                className="text-[#00C896] text-[10px] font-bold hover:opacity-70">
                {editCountries ? "Cancel" : "Edit"}
              </button>
            </div>

            {editCountries ? (
              <div className="space-y-3">
                <div>
                  <p className="text-white/30 text-xs mb-1">
                    Whitelist — only these countries can use this service
                    <span className="ml-1 text-white/20">(empty = all countries)</span>
                  </p>
                  <input
                    value={allowedDraft}
                    onChange={e => setAllowedDraft(e.target.value)}
                    placeholder="NG, GH, KE (comma-separated ISO codes)"
                    className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50"
                  />
                </div>
                <div>
                  <p className="text-white/30 text-xs mb-1">
                    Blacklist — these countries are always blocked
                  </p>
                  <input
                    value={disabledDraft}
                    onChange={e => setDisabledDraft(e.target.value)}
                    placeholder="ZW, SD (comma-separated ISO codes)"
                    className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50"
                  />
                </div>
                <button onClick={handleSaveCountries} disabled={savingCountries}
                  className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                  {savingCountries
                    ? <><div className="w-3 h-3 border-2 border-black/20 border-t-black rounded-full animate-spin" />Saving…</>
                    : "Save Country Rules"}
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                {svc.allowedCountries?.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#00C896] font-semibold">Whitelist:</span>
                    <div className="flex gap-1 flex-wrap">
                      {svc.allowedCountries.map(c => (
                        <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-[#00C896]/10 text-[#00C896] font-bold">{c}</span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-white/25 text-xs">All countries allowed</p>
                )}
                {svc.disabledCountries?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-red-400 font-semibold">Blocked:</span>
                    <div className="flex gap-1 flex-wrap">
                      {svc.disabledCountries.map(c => (
                        <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-bold">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Per-user blocking */}
          <div>
            <p className="text-white/40 text-[10px] uppercase tracking-wide font-semibold mb-2">Block Specific Users</p>

            {/* Currently blocked users */}
            {svc.disabledUsers?.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {svc.disabledUsers.map(uid => (
                  <div key={uid} className="flex items-center justify-between bg-red-500/8 border border-red-500/15 rounded-xl px-3 py-2">
                    <span className="text-red-400 text-xs font-mono">{uid}</span>
                    <button onClick={() => handleUnblockUser(uid)}
                      className="text-white/30 hover:text-[#00C896] text-[10px] font-bold transition-colors">
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add user to block list */}
            <div className="flex gap-2">
              <input
                value={blockUserId}
                onChange={e => setBlockUserId(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleBlockUser()}
                placeholder="User ID to block"
                className="flex-1 bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-red-500/50"
              />
              <button onClick={handleBlockUser} disabled={blockingUser || !blockUserId.trim()}
                className="px-4 py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-400 text-xs font-bold disabled:opacity-30 transition-colors flex-shrink-0">
                {blockingUser ? "…" : "Block"}
              </button>
            </div>
            <p className="text-white/20 text-[10px] mt-1">
              Paste the user's userId from the Users section to block them from this service only.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ServicesView() {
  const [services, setServices]   = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [toggling, setToggling]   = useState(null);
  const [confirm,  setConfirm]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminServices();
      if (res.status) setServices(res.data);
    } catch { toast.error("Failed to load services"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = (key, newState, name) => {
    setConfirm({
      key,
      newState,
      title:        `${newState ? "Enable" : "Disable"} ${name}`,
      message:      newState
        ? `Re-enable ${name}? Users will immediately regain access.`
        : `Disable ${name}? All related features will be hidden from users immediately.`,
      confirmLabel: newState ? "Enable" : "Disable",
      confirmColor: newState ? "green" : "red",
    });
  };

  const executeToggle = async () => {
    const { key, newState } = confirm;
    setToggling(key);
    setConfirm(null);
    try {
      const res = await updateAdminService(key, { enabled: newState });
      if (res.status) {
        setServices(prev => prev.map(s => s.key === key ? { ...s, enabled: newState } : s));
        toast.success(res.message);
      }
    } catch { toast.error("Failed to update service"); }
    finally { setToggling(null); }
  };

  const handleToggleAll = (enable) => {
    setConfirm({
      all:          true,
      newState:     enable,
      title:        enable ? "Enable All Services" : "⚠️ Disable All Services",
      message:      enable
        ? "Re-enable all platform services? Everything will become available to users."
        : "Disable ALL services? This will put the entire platform in maintenance mode.",
      confirmLabel: enable ? "Enable All" : "Disable All",
      confirmColor: enable ? "green" : "red",
    });
  };

  const executeToggleAll = async () => {
    const { newState } = confirm;
    setToggling("all");
    setConfirm(null);
    try {
      await toggleAllServices(newState);
      setServices(prev => prev.map(s => ({ ...s, enabled: newState })));
      toast.success(`All services ${newState ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to update all services"); }
    finally { setToggling(null); }
  };

  const activeCount   = services.filter(s => s.enabled).length;
  const disabledCount = services.filter(s => !s.enabled).length;
  const allEnabled    = services.every(s => s.enabled);
  const allDisabled   = services.every(s => !s.enabled);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Platform Services</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {activeCount} active · {disabledCount} disabled
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleToggleAll(false)}
            disabled={allDisabled || !!toggling}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500/10 text-red-400 border border-red-500/20 text-xs font-bold hover:bg-red-500/20 transition-colors disabled:opacity-30"
          >
            <i className="ri-stop-circle-line" />Disable All
          </button>
          <button
            onClick={() => handleToggleAll(true)}
            disabled={allEnabled || !!toggling}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#00C896]/10 text-[#00C896] border border-[#00C896]/20 text-xs font-bold hover:bg-[#00C896]/20 transition-colors disabled:opacity-30"
          >
            <i className="ri-play-circle-line" />Enable All
          </button>
        </div>
      </div>

      {/* Status summary */}
      {disabledCount > 0 && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3">
          <i className="ri-alert-line text-red-400 text-lg flex-shrink-0" />
          <div>
            <p className="text-red-400 text-sm font-semibold">
              {disabledCount} service{disabledCount > 1 ? "s" : ""} currently disabled
            </p>
            <p className="text-red-400/60 text-xs mt-0.5">
              {services.filter(s => !s.enabled).map(s => s.name).join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Service cards */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-28 bg-[#161616] rounded-2xl border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {services.map(svc => (
            <ServiceCard
              key={svc.key}
              svc={svc}
              onToggle={handleToggle}
              toggling={toggling}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmColor={confirm.confirmColor}
          loading={!!toggling}
          onConfirm={confirm.all ? executeToggleAll : executeToggle}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
